import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SortableRepeater } from './PlaylistComponents';

export 
function renderFormField(field, locked, formResponses, handleFormInputChange, handleRepeaterChange, addRepeaterRow, removeRepeaterRow, reorderRepeaterRows, handleColumnChange, primaryColor) {
  const value = formResponses[field.name] || '';
  
  switch (field.type) {
    case 'section':
      return (
        <div className="py-4 border-t border-white/20">
          <h3 className="text-lg font-semibold" style={{ color: primaryColor }}>{field.label}</h3>
          {field.placeholder && <p className="text-white/50 text-sm">{field.placeholder}</p>}
        </div>
      );
    
    case 'comment':
      return (
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <p className="text-white/70" style={{ color: primaryColor }}>{field.label}</p>
          {field.placeholder && <p className="text-white/50 text-sm mt-1">{field.placeholder}</p>}
        </div>
      );
    
    case 'time':
      return (
        <Input
          type="time"
          value={value}
          onChange={(e) => handleFormInputChange(field.name, e.target.value)}
          disabled={locked}
          className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}
        />
      );
    
    case 'textarea':
      return (
        <textarea
          value={value}
          onChange={(e) => handleFormInputChange(field.name, e.target.value)}
          placeholder={field.placeholder}
          disabled={locked}
          className={`w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 min-h-[100px] ${locked ? 'opacity-60' : ''}`}
        />
      );
    
    case 'select':
      return (
        <Select value={value} onValueChange={(v) => handleFormInputChange(field.name, v)} disabled={locked}>
          <SelectTrigger className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}>
            <SelectValue placeholder={field.placeholder || 'Select...'} />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-white/20">
            {field.options?.map((opt) => (
              <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    
    case 'repeater':
      const rows = formResponses[field.name] || [{ col1: '', col2: '' }];
      return (
        <SortableRepeater
          field={field}
          rows={rows}
          locked={locked}
          handleRepeaterChange={handleRepeaterChange}
          addRepeaterRow={addRepeaterRow}
          removeRepeaterRow={removeRepeaterRow}
          reorderRepeaterRows={reorderRepeaterRows}
          is3Col={false}
        />
      );
    
    case 'repeater3':
      const rows3 = formResponses[field.name] || [{ col1: '', col2: '', col3: '' }];
      return (
        <SortableRepeater
          field={field}
          rows={rows3}
          locked={locked}
          handleRepeaterChange={handleRepeaterChange}
          addRepeaterRow={addRepeaterRow}
          removeRepeaterRow={removeRepeaterRow}
          reorderRepeaterRows={reorderRepeaterRows}
          is3Col={true}
        />
      );
    
    case 'columns2':
      const col2Value = formResponses[field.name] || { col1: '', col2: '' };
      
      // Helper to render typed column input for columns2
      const renderCol2Input = (colKey, colType, colOptions, colLabel) => {
        const colValue = col2Value[colKey] || '';
        
        if (colType === 'select' && colOptions && colOptions.length > 0) {
          return (
            <Select value={colValue} onValueChange={(v) => handleColumnChange(field.name, colKey, v)} disabled={locked}>
              <SelectTrigger className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}>
                <SelectValue placeholder={colLabel || 'Select...'} />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/20">
                {colOptions.map((opt) => (
                  <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        
        if (colType === 'date') {
          return (
            <Input
              type="date"
              value={colValue}
              onChange={(e) => handleColumnChange(field.name, colKey, e.target.value)}
              disabled={locked}
              className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}
            />
          );
        }
        
        if (colType === 'time') {
          return (
            <Input
              type="time"
              value={colValue}
              onChange={(e) => handleColumnChange(field.name, colKey, e.target.value)}
              disabled={locked}
              className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}
            />
          );
        }
        
        // Default to text input
        return (
          <Input
            value={colValue}
            onChange={(e) => handleColumnChange(field.name, colKey, e.target.value)}
            placeholder={colLabel}
            disabled={locked}
            className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}
          />
        );
      };
      
      return (
        <div className="grid grid-cols-2 gap-4">
          {renderCol2Input('col1', field.column1Type, field.column1Options, field.column1Label)}
          {renderCol2Input('col2', field.column2Type, field.column2Options, field.column2Label)}
        </div>
      );
    
    case 'columns3':
      const col3Value = formResponses[field.name] || { col1: '', col2: '', col3: '' };
      
      // Helper to render typed column input
      const renderCol3Input = (colKey, colType, colOptions, colLabel) => {
        const colValue = col3Value[colKey] || '';
        
        if (colType === 'select' && colOptions && colOptions.length > 0) {
          return (
            <Select value={colValue} onValueChange={(v) => handleColumnChange(field.name, colKey, v)} disabled={locked}>
              <SelectTrigger className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}>
                <SelectValue placeholder={colLabel || 'Select...'} />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/20">
                {colOptions.map((opt) => (
                  <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        
        if (colType === 'date') {
          return (
            <Input
              type="date"
              value={colValue}
              onChange={(e) => handleColumnChange(field.name, colKey, e.target.value)}
              disabled={locked}
              className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}
            />
          );
        }
        
        if (colType === 'time') {
          return (
            <Input
              type="time"
              value={colValue}
              onChange={(e) => handleColumnChange(field.name, colKey, e.target.value)}
              disabled={locked}
              className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}
            />
          );
        }
        
        if (colType === 'number') {
          return (
            <Input
              type="number"
              value={colValue}
              onChange={(e) => handleColumnChange(field.name, colKey, e.target.value)}
              placeholder={colLabel}
              disabled={locked}
              className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}
            />
          );
        }
        
        // Default to text input
        return (
          <Input
            value={colValue}
            onChange={(e) => handleColumnChange(field.name, colKey, e.target.value)}
            placeholder={colLabel}
            disabled={locked}
            className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}
          />
        );
      };
      
      return (
        <div className="grid grid-cols-3 gap-4">
          {renderCol3Input('col1', field.column1Type, field.column1Options, field.column1Label)}
          {renderCol3Input('col2', field.column2Type, field.column2Options, field.column2Label)}
          {renderCol3Input('col3', field.column3Type, field.column3Options, field.column3Label)}
        </div>
      );
    
    case 'checkbox':
      return (
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={value === true || value === 'true'}
            onChange={(e) => handleFormInputChange(field.name, e.target.checked)}
            disabled={locked}
            className={`w-5 h-5 rounded border-white/30 bg-white/10 text-primary focus:ring-primary ${locked ? 'opacity-60' : ''}`}
          />
          <span className="text-white/80 text-sm">{field.placeholder || 'Yes'}</span>
        </div>
      );
    
    case 'checkbox_conditional':
      const isChecked = value?.checked === true || value?.checked === 'true';
      const conditionalValue = value?.conditionalValue || '';
      
      const renderConditionalInput = () => {
        const cfType = field.conditionalFieldType || 'text';
        const cfPlaceholder = field.conditionalFieldPlaceholder || '';
        const cfOptions = field.conditionalFieldOptions || [];
        
        if (cfType === 'select') {
          return (
            <Select 
              value={conditionalValue} 
              onValueChange={(v) => handleFormInputChange(field.name, { checked: true, conditionalValue: v })} 
              disabled={locked}
            >
              <SelectTrigger className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}>
                <SelectValue placeholder={cfPlaceholder || 'Select...'} />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/20">
                {cfOptions.map((opt) => (
                  <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        
        if (cfType === 'textarea') {
          return (
            <textarea
              value={conditionalValue}
              onChange={(e) => handleFormInputChange(field.name, { checked: true, conditionalValue: e.target.value })}
              placeholder={cfPlaceholder}
              disabled={locked}
              className={`w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 min-h-[80px] ${locked ? 'opacity-60' : ''}`}
            />
          );
        }
        
        return (
          <Input
            type={cfType === 'number' ? 'number' : cfType === 'date' ? 'date' : cfType === 'time' ? 'time' : 'text'}
            value={conditionalValue}
            onChange={(e) => handleFormInputChange(field.name, { checked: true, conditionalValue: e.target.value })}
            placeholder={cfPlaceholder}
            disabled={locked}
            className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}
          />
        );
      };
      
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => handleFormInputChange(field.name, { 
                checked: e.target.checked, 
                conditionalValue: e.target.checked ? conditionalValue : '' 
              })}
              disabled={locked}
              className={`w-5 h-5 rounded border-white/30 bg-white/10 text-primary focus:ring-primary ${locked ? 'opacity-60' : ''}`}
            />
            <span className="text-white/80 text-sm">{field.placeholder || 'Yes'}</span>
          </div>
          {isChecked && (
            <div className="ml-8 p-3 rounded-lg bg-white/5 border border-white/10 space-y-2">
              <label className="text-sm text-white/70">{field.conditionalFieldLabel}</label>
              {renderConditionalInput()}
            </div>
          )}
        </div>
      );
    
    default:
      const safeValue = typeof value === 'object' ? '' : value;
      return (
        <Input
          type={field.type === 'email' ? 'email' : field.type === 'tel' ? 'tel' : field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          value={safeValue}
          onChange={(e) => handleFormInputChange(field.name, e.target.value)}
          placeholder={field.placeholder}
          disabled={locked}
          className={`bg-white/10 border-white/20 text-white ${locked ? 'opacity-60' : ''}`}
        />
      );
  }
}
